/**
 * iter-119aa (G1 + G6) — Per-dashboard connector health pill.
 *
 * Calls `/api/dashboards/{id}/source-health` once per dashboard load
 * (cheap aggregation against `model_rows` + a single `data_connectors`
 * lookup; no joined-query so it's not on the cache hot path) and
 * renders a compact summary chip:
 *
 *   ◉ 3 healthy · 1 stale · 0 failed       (G1)
 *   +2 accounts                            (G6)
 *
 * Clicking expands to a popover showing every source by name, driver,
 * last-run timestamp, and the most recent error message (if any) so an
 * admin can spot a broken sync without leaving the dashboard.
 *
 * Visibility rules:
 *   • Hidden entirely when the dashboard has 0 attached/rendering
 *     sources (would just say "0 healthy" — noise).
 *   • Only renders the multi-account badge when total > 1.
 */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import {
    Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { CheckCircle2, AlertTriangle, XCircle, Clock, Layers } from "lucide-react";

const _ICONS = {
    healthy: <CheckCircle2 className="h-3 w-3 text-emerald-500" />,
    stale:   <Clock className="h-3 w-3 text-amber-500" />,
    failed:  <XCircle className="h-3 w-3 text-red-500" />,
    unknown: <AlertTriangle className="h-3 w-3 text-muted-foreground" />,
};

export default function ConnectorHealthPill({ dashboardId }) {
    const [data, setData] = useState(null);
    const [open, setOpen] = useState(false);
    useEffect(() => {
        if (!dashboardId) return;
        let mounted = true;
        api.get(`/dashboards/${dashboardId}/source-health`)
           .then((r) => { if (mounted) setData(r.data); })
           .catch(() => { if (mounted) setData(null); });
        return () => { mounted = false; };
    }, [dashboardId]);

    if (!data || data.total === 0) return null;
    const { sources, total, healthy, stale, failed, account_count } = data;

    // Pick the worst-case status to colour the pill bezel.
    const worst = failed > 0 ? "failed" : stale > 0 ? "stale" : "healthy";
    const colour = {
        healthy: "border-emerald-500/40 text-emerald-700 dark:text-emerald-400",
        stale:   "border-amber-500/40 text-amber-700 dark:text-amber-400",
        failed:  "border-red-500/40 text-red-700 dark:text-red-400",
    }[worst];

    return (
        <div className="inline-flex items-center gap-1.5" data-testid="connector-health-pill">
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <button
                        className={`h-7 px-2 inline-flex items-center gap-1.5 border rounded-sm text-[11px] font-mono uppercase tracking-wider hover:bg-secondary/40 transition-colors ${colour}`}
                        title="Click for per-source health details"
                        data-testid="connector-health-trigger"
                    >
                        {_ICONS[worst]}
                        <span>{healthy}/{total}</span>
                        {stale > 0 && <span className="opacity-70">· {stale} stale</span>}
                        {failed > 0 && <span className="opacity-70">· {failed} failed</span>}
                    </button>
                </PopoverTrigger>
                <PopoverContent align="end" className="w-[320px] p-0" data-testid="connector-health-popover">
                    <div className="px-3 py-2 border-b border-border">
                        <div className="text-xs font-medium">Source health</div>
                        <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mt-0.5">
                            {healthy} HEALTHY · {stale} STALE · {failed} FAILED
                        </div>
                    </div>
                    <div className="max-h-[300px] overflow-y-auto">
                        {sources.map((s) => (
                            <div key={s.id} className="px-3 py-2 border-b border-border last:border-0 text-xs"
                                 data-testid={`connector-health-row-${s.id}`}>
                                <div className="flex items-center gap-2">
                                    {_ICONS[s.level]}
                                    <span className="font-medium truncate">{s.name || s.id}</span>
                                    <Badge variant="outline" className="text-[9px] font-mono ml-auto">
                                        {s.driver}
                                    </Badge>
                                </div>
                                <div className="text-[10px] font-mono text-muted-foreground mt-1">
                                    {s.last_run_at ? `Last run: ${new Date(s.last_run_at).toLocaleString()}` : "No runs yet"}
                                </div>
                                {s.last_error && (
                                    <div className="text-[10px] text-red-600 dark:text-red-400 mt-1 line-clamp-2">
                                        {s.last_error}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </PopoverContent>
            </Popover>
            {account_count >= 1 && (
                <Badge variant="outline" className="text-[10px] font-mono"
                       title={account_count > 1
                            ? `Rolling up data from ${account_count} accounts/sources`
                            : `Data from 1 account`}
                       data-testid="connector-rollup-badge">
                    <Layers className="h-3 w-3 mr-1" />
                    {account_count === 1 ? "1 account" : `${account_count} accounts`}
                </Badge>
            )}
        </div>
    );
}
